import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ModeCardTheme = 'blue' | 'purple';

interface ModeCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  hint?: string;
  selected: boolean;
  onSelect: () => void;
  iconColor?: string;
  iconBgColor?: string;
  theme?: ModeCardTheme;
}

const THEMES: Record<ModeCardTheme, {
  border: string;
  bg: string;
  iconBg: string;
  iconText: string;
  title: string;
  desc: string;
  hint: string;
}> = {
  blue: {
    border: 'border-blue-500',
    bg: 'bg-blue-50',
    iconBg: 'bg-blue-100',
    iconText: 'text-blue-600',
    title: 'text-blue-900',
    desc: 'text-blue-700',
    hint: 'text-blue-600',
  },
  purple: {
    border: 'border-purple-500',
    bg: 'bg-purple-50',
    iconBg: 'bg-purple-100',
    iconText: 'text-purple-600',
    title: 'text-purple-900',
    desc: 'text-purple-700',
    hint: 'text-purple-600',
  },
};

export function ModeCard({
  icon: Icon,
  title,
  description,
  hint,
  selected,
  onSelect,
  iconColor,
  iconBgColor,
  theme = 'blue',
}: ModeCardProps) {
  const t = THEMES[theme];
  const restingIconColor = iconColor ?? t.iconText;
  const restingIconBg = iconBgColor ?? t.iconBg;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'w-full text-left rounded-lg border-2 p-4 transition-all tap-highlight-transparent',
        'active:scale-[0.98]',
        selected
          ? cn(t.border, t.bg)
          : 'border-gray-200 bg-white hover:border-gray-300 active:bg-gray-50'
      )}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'w-12 h-12 rounded-lg flex items-center justify-center shrink-0',
            selected ? t.iconBg : restingIconBg
          )}
        >
          <Icon
            className={cn(
              'w-6 h-6',
              selected ? t.iconText : restingIconColor
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <p
            className={cn(
              'font-semibold text-base',
              selected ? t.title : 'text-gray-900'
            )}
          >
            {title}
          </p>
          <p
            className={cn(
              'text-sm mt-0.5',
              selected ? t.desc : 'text-gray-500'
            )}
          >
            {description}
          </p>
          {hint && (
            <p
              className={cn(
                'text-xs mt-2 italic',
                selected ? t.hint : 'text-gray-400'
              )}
            >
              {hint}
            </p>
          )}
        </div>
      </div>
    </button>
  );
}
